import { useTheme } from "next-themes";
import { useCallback, useEffect, useState } from "react";
import Joyride, { type CallBackProps, STATUS, type Step } from "react-joyride";
import { useStore } from "zustand";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

const TOUR_START_DELAY_MS = 500;

const tourSteps: Step[] = [
	{
		target: "[data-tour='workspace-selector']",
		content: (
			<div style={{ textAlign: "left" }}>
				<strong>Welcome!</strong> This is where you switch between the How to
				Change the World program workspaces you are part of. If this is your
				first program, then you will only see one program workspace.
			</div>
		),
		placement: "right",
		disableBeacon: true,
	},
	{
		target: "[data-tour='channels-section']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>Channels are where learning and conversations happen.</strong>
				<br />
				Click a Cohort or Team channel to join the video channel and speak live
				with colleagues and the teaching team. Click the chat bubble icon to
				open the text channel and write messages.
			</div>
		),
		placement: "right",
	},
	{
		target: "[data-tour='channels-section']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>Some channels are text only.</strong>
				<br />
				You can post in most of them, except for Broadcast channels that are
				reserved for the Program and Teaching Team to share information widely.
			</div>
		),
		placement: "right",
	},
	{
		target: "[data-tour='channels-section']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				Unread messages will be visible with highlighting, bolding and a filled
				in chat bubble.
			</div>
		),
		placement: "right",
	},
	{
		target: "[data-tour='channels-section']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>
					Use the arrows to collapse channel categories you are not using.
				</strong>
				<br />
				The category name will be highlighted and bolded if you have unread
				messages in any channel in that category.
			</div>
		),
		placement: "right",
	},
	{
		target: "[data-tour='channels-section']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				If you hold down CMD (Mac) or CTRL (PC) when you click a channel, it
				will open a new tab in the main window.
			</div>
		),
		placement: "right",
	},
	{
		target: "[data-tour='tabs-click']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				You can have multiple channels open in different tabs at the same time.
				Hold Cmd/Ctrl and click a channel to open it in a new tab.
			</div>
		),
		placement: "bottom",
	},
	{
		target: "[data-tour='main-grid']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>This is the main interaction space.</strong>
				<br />
				Any video or text channel you open will appear here.
			</div>
		),
		placement: "bottom",
	},
	{
		target: "[data-tour='main-grid']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>
					When you navigate away from a live video channel, it will pop out to a
					Picture-in-Picture (PiP) window so you don't lose track that you are
					in a video channel.
				</strong>
				<br />
				Please note: if you open another PiP window from YouTube, Google Meet or
				similar, that will disrupt the PiP from this platform.
			</div>
		),
		placement: "bottom",
	},
	{
		target: "[data-tour='tabs-click']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>
					You can always keep multiple tabs open and move between them.
				</strong>
				<br />
				Remember: you can open any voice or text channel in a new tab by holding
				down CMD (Mac) or CTRL (PC) when clicking the channel with your mouse.
			</div>
		),
		placement: "bottom",
	},
	{
		target: "[data-tour='documents-sidebar']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>Find your program, cohort and team resources here.</strong>
				<br />
				Access program resources and materials from here. Most will open in a
				new tab in your browser.
			</div>
		),
		placement: "left",
	},
	{
		target: "[data-tour='user-menu']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>Manage your experience here.</strong>
				<br />
				Update your settings, change themes, log out or restart this tour
				anytime.
			</div>
		),
		placement: "top",
	},
];

const messageTourSteps: Step[] = [
	{
		target: "[data-tour='message-input']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>Type your messages here.</strong>
				<br />
				You can @mention users, add emojis and reply to messages. Press Cmd/Ctrl
				+ Enter to send.
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
];

const voiceTourSteps: Step[] = [
	{
		target: "[data-tour='voice-controls']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>You are now in a live Video channel.</strong>
				<br />
				Control your microphone, camera, screen sharing or leave the call using
				these buttons. Your audio and camera settings are available in the menu
				next to the microphone and camera buttons.
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: "[data-tour='pip-button']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>
					Pop out the video channel into a Picture-in-Picture (PiP) window.
				</strong>
				<br />
				You can pop this video channel into a PiP using this button. This will
				also happen automatically when you navigate to a text channel or another
				tab within the platform.
			</div>
		),
		placement: "bottom",
	},
];

const broadcastTourSteps: Step[] = [
	{
		target: "[data-tour='broadcast-layout']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>Broadcast + Video combined view.</strong>
				<br />
				When the Broadcast is live, your screen will show both the Broadcast and
				your Video channel together.
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: "[data-tour='broadcast-orientation']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>Choose your preferred layout.</strong>
				<br />
				Click here to switch between different layouts: Split (vertical or
				horizontal), Tabs (switch between views), or Spotlight (one view
				fullscreen with the other in a small overlay).
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: "[data-tour='broadcast-tabs']",
		content: (
			<div style={{ textAlign: "left", fontFamily: "Roboto, sans-serif" }}>
				<strong>Switch between views.</strong>
				<br />
				Click these tabs to switch between the Video channel and the Broadcast.
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
];

export function AppTour() {
	const runTour = useStore(uiStore, (state) => state.runTour);
	const stopTour = useStore(uiStore, (state) => state.stopTour);
	const tourPreferences = useStore(coreStore, (state) => state.tourPreferences);
	const updateTourStatus = useStore(
		coreStore,
		(state) => state.updateTourStatus
	);
	const channelsMap = useStore(coreStore, (state) => state.channels);
	const [run, setRun] = useState(false);
	const { theme } = useTheme();

	useEffect(() => {
		// The tour targets channel UI, so it cannot start before channels load.
		if (channelsMap.size === 0) return;

		if (runTour) {
			setRun(true);
			return;
		}

		const hasCompletedTour = tourPreferences?.hasCompletedAppTour ?? true;
		if (!hasCompletedTour && tourPreferences !== null) {
			const timer = setTimeout(() => setRun(true), TOUR_START_DELAY_MS);
			return () => clearTimeout(timer);
		}
		return;
	}, [runTour, tourPreferences, channelsMap]);

	const handleJoyrideCallback = useCallback(
		(data: CallBackProps) => {
			const { status } = data;
			const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

			if (finishedStatuses.includes(status)) {
				setRun(false);
				updateTourStatus({ tourType: "app", completed: true });
				stopTour();
			}
		},
		[stopTour, updateTourStatus]
	);

	const isDark = theme === "dark";

	return (
		<Joyride
			callback={handleJoyrideCallback}
			continuous
			disableScrolling
			hideCloseButton
			run={run}
			showProgress
			showSkipButton
			steps={tourSteps}
			styles={{
				options: {
					arrowColor: isDark ? "#1c1c1c" : "#ffffff",
					backgroundColor: isDark ? "#1c1c1c" : "#ffffff",
					overlayColor: "rgba(0, 0, 0, 0.5)",
					primaryColor: isDark ? "#3b82f6" : "#2563eb",
					textColor: isDark ? "#e5e5e5" : "#171717",
					zIndex: 10_000,
				},
				tooltip: {
					borderRadius: 8,
					padding: 16,
				},
				buttonNext: {
					borderRadius: 6,
					padding: "8px 16px",
				},
				buttonBack: {
					borderRadius: 6,
					marginRight: 8,
				},
				buttonSkip: {
					borderRadius: 6,
				},
			}}
		/>
	);
}

export function MessageTour() {
	const runTour = useStore(uiStore, (state) => state.runTour);
	const stopTour = useStore(uiStore, (state) => state.stopTour);
	const tourPreferences = useStore(coreStore, (state) => state.tourPreferences);
	const updateTourStatus = useStore(
		coreStore,
		(state) => state.updateTourStatus
	);
	const [run, setRun] = useState(false);
	const { theme } = useTheme();

	useEffect(() => {
		if (runTour) {
			setRun(true);
			return;
		}

		const hasCompletedAppTour = tourPreferences?.hasCompletedAppTour;
		const hasCompletedMessageTour = tourPreferences?.hasCompletedMessageTour;

		if (
			tourPreferences !== null &&
			hasCompletedAppTour === true &&
			hasCompletedMessageTour === false
		) {
			const timer = setTimeout(() => setRun(true), TOUR_START_DELAY_MS);
			return () => clearTimeout(timer);
		}
		return;
	}, [runTour, tourPreferences]);

	const handleJoyrideCallback = useCallback(
		(data: CallBackProps) => {
			const { status } = data;
			const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

			if (finishedStatuses.includes(status)) {
				setRun(false);
				updateTourStatus({ tourType: "message", completed: true });
				stopTour();
			}
		},
		[stopTour, updateTourStatus]
	);

	const isDark = theme === "dark";

	return (
		<Joyride
			callback={handleJoyrideCallback}
			continuous
			disableScrolling
			hideCloseButton
			run={run}
			showProgress
			showSkipButton
			steps={messageTourSteps}
			styles={{
				options: {
					arrowColor: isDark ? "#1c1c1c" : "#ffffff",
					backgroundColor: isDark ? "#1c1c1c" : "#ffffff",
					overlayColor: "rgba(0, 0, 0, 0.5)",
					primaryColor: isDark ? "#3b82f6" : "#2563eb",
					textColor: isDark ? "#e5e5e5" : "#171717",
					zIndex: 10_000,
				},
				tooltip: {
					borderRadius: 8,
					padding: 16,
				},
				buttonNext: {
					borderRadius: 6,
					padding: "8px 16px",
				},
				buttonBack: {
					borderRadius: 6,
					marginRight: 8,
				},
				buttonSkip: {
					borderRadius: 6,
				},
			}}
		/>
	);
}

export function VoiceTour() {
	const runTour = useStore(uiStore, (state) => state.runTour);
	const stopTour = useStore(uiStore, (state) => state.stopTour);
	const tourPreferences = useStore(coreStore, (state) => state.tourPreferences);
	const updateTourStatus = useStore(
		coreStore,
		(state) => state.updateTourStatus
	);
	const [run, setRun] = useState(false);
	const { theme } = useTheme();

	useEffect(() => {
		if (runTour) {
			setRun(true);
			return;
		}

		const hasCompletedVoiceTour =
			tourPreferences?.hasCompletedVoiceTour ?? true;
		if (!hasCompletedVoiceTour && tourPreferences !== null) {
			const timer = setTimeout(() => setRun(true), TOUR_START_DELAY_MS);
			return () => clearTimeout(timer);
		}
		return;
	}, [runTour, tourPreferences]);

	const handleJoyrideCallback = useCallback(
		(data: CallBackProps) => {
			const { status } = data;
			const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

			if (finishedStatuses.includes(status)) {
				setRun(false);
				updateTourStatus({ tourType: "voice", completed: true });
				stopTour();
			}
		},
		[stopTour, updateTourStatus]
	);

	const isDark = theme === "dark";

	return (
		<Joyride
			callback={handleJoyrideCallback}
			continuous
			disableScrolling
			hideCloseButton
			run={run}
			showProgress
			showSkipButton
			steps={voiceTourSteps}
			styles={{
				options: {
					arrowColor: isDark ? "#1c1c1c" : "#ffffff",
					backgroundColor: isDark ? "#1c1c1c" : "#ffffff",
					overlayColor: "rgba(0, 0, 0, 0.5)",
					primaryColor: isDark ? "#3b82f6" : "#2563eb",
					textColor: isDark ? "#e5e5e5" : "#171717",
					zIndex: 10_000,
				},
				tooltip: {
					borderRadius: 8,
					padding: 16,
				},
				buttonNext: {
					borderRadius: 6,
					padding: "8px 16px",
				},
				buttonBack: {
					borderRadius: 6,
					marginRight: 8,
				},
				buttonSkip: {
					borderRadius: 6,
				},
			}}
		/>
	);
}

export function BroadcastTour() {
	const runTour = useStore(uiStore, (state) => state.runTour);
	const stopTour = useStore(uiStore, (state) => state.stopTour);
	const tourPreferences = useStore(coreStore, (state) => state.tourPreferences);
	const updateTourStatus = useStore(
		coreStore,
		(state) => state.updateTourStatus
	);
	const [run, setRun] = useState(false);
	const { theme } = useTheme();

	useEffect(() => {
		if (runTour) {
			setRun(true);
			return;
		}

		const hasCompletedBroadcastTour =
			tourPreferences?.hasCompletedBroadcastTour ?? true;
		if (!hasCompletedBroadcastTour && tourPreferences !== null) {
			const broadcastLayoutElement = document.querySelector(
				"[data-tour='broadcast-layout']"
			);
			if (broadcastLayoutElement) {
				const timer = setTimeout(() => setRun(true), TOUR_START_DELAY_MS);
				return () => clearTimeout(timer);
			}
		}
		return;
	}, [runTour, tourPreferences]);

	const handleJoyrideCallback = useCallback(
		(data: CallBackProps) => {
			const { status } = data;
			const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

			if (finishedStatuses.includes(status)) {
				setRun(false);
				updateTourStatus({ tourType: "broadcast", completed: true });
				stopTour();
			}
		},
		[stopTour, updateTourStatus]
	);

	const isDark = theme === "dark";

	return (
		<Joyride
			callback={handleJoyrideCallback}
			continuous
			disableScrolling
			hideCloseButton
			run={run}
			showProgress
			showSkipButton
			steps={broadcastTourSteps}
			styles={{
				options: {
					arrowColor: isDark ? "#1c1c1c" : "#ffffff",
					backgroundColor: isDark ? "#1c1c1c" : "#ffffff",
					overlayColor: "rgba(0, 0, 0, 0.5)",
					primaryColor: isDark ? "#3b82f6" : "#2563eb",
					textColor: isDark ? "#e5e5e5" : "#171717",
					zIndex: 10_000,
				},
				tooltip: {
					borderRadius: 8,
					padding: 16,
				},
				buttonNext: {
					borderRadius: 6,
					padding: "8px 16px",
				},
				buttonBack: {
					borderRadius: 6,
					marginRight: 8,
				},
				buttonSkip: {
					borderRadius: 6,
				},
			}}
		/>
	);
}
